import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import "./whatsappVerify.css";

/**
 * Reverse-OTP WhatsApp verification, reusable wherever we need the user to prove
 * they own a number: the post-signup prompt and the publish gate both mount this.
 *
 * We never message the user — they send a code to us from their own WhatsApp, and
 * the sender's number is the proof. So the component's whole job is: mint a code,
 * hand over a wa.me deep link, and poll until the listener reports the message
 * arrived. See server/controllers/whatsappVerification.js.
 *
 * Props:
 *   onVerified  — called once when status flips to verified
 *   title/subtitle — copy, so the same flow can read as "onboarding" or "gate"
 *   autoStart   — mint the code on mount instead of behind a button
 */
function WhatsAppVerify({ onVerified, title, subtitle, autoStart = false }) {
  const [challenge, setChallenge] = useState(null); // { code, waLink, listenerNumber }
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");

  const start = useCallback(async () => {
    setStarting(true);
    setError("");
    try {
      const res = await axios.post("/api/v1/user/whatsapp/start");
      setChallenge(res.data);
    } catch (err) {
      // 503 = the listener number isn't configured / listener is down. Say so
      // plainly rather than handing out a code that can never be received.
      setError(
        err.response?.data?.message ||
          "WhatsApp verification is unavailable right now. Please try again later."
      );
    } finally {
      setStarting(false);
    }
  }, []);

  useEffect(() => {
    if (autoStart) start();
  }, [autoStart, start]);

  // Poll while a challenge is open. The socket push is the primary signal, but a
  // user who switched to WhatsApp may return to a dropped socket, so this is the
  // reliable fallback. Stops when the code expires.
  useEffect(() => {
    if (!challenge) return undefined;
    const id = setInterval(async () => {
      try {
        const res = await axios.get("/api/v1/user/whatsapp/status");
        if (res.data.status === "verified") {
          clearInterval(id);
          if (onVerified) onVerified();
        }
      } catch (e) {
        /* transient — keep polling until the challenge expires */
      }
    }, 3000);
    const stop = setTimeout(() => {
      clearInterval(id);
      setChallenge(null);
    }, 10 * 60 * 1000);
    return () => {
      clearInterval(id);
      clearTimeout(stop);
    };
  }, [challenge, onVerified]);

  return (
    <div className="sr-waverify">
      <div className="sr-waverify__head">
        <span className="sr-waverify__icon">
          <FontAwesomeIcon icon={faWhatsapp} />
        </span>
        <div>
          <h3 className="sr-waverify__title">{title || "Verify your WhatsApp"}</h3>
          {subtitle && <p className="sr-waverify__sub">{subtitle}</p>}
        </div>
      </div>

      {error && <p className="sr-waverify__error">{error}</p>}

      {!challenge ? (
        <button
          type="button"
          className="sr-waverify__btn"
          onClick={start}
          disabled={starting}
        >
          {starting ? "Starting…" : "Verify WhatsApp"}
        </button>
      ) : (
        <>
          <p className="sr-waverify__instruction">
            Send this code from the WhatsApp number you want verified — we never
            message you first:
          </p>
          <div className="sr-waverify__code">{challenge.code}</div>
          <a
            className="sr-waverify__btn"
            href={challenge.waLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FontAwesomeIcon icon={faWhatsapp} /> Open WhatsApp
          </a>
          <p className="sr-waverify__sub">
            Or send <strong>{challenge.code}</strong> to{" "}
            <strong>+{challenge.listenerNumber}</strong>. Waiting for your message…
            this code expires in 10 minutes.
          </p>
        </>
      )}
    </div>
  );
}

export default WhatsAppVerify;
